/**
 * TextOperations - Utility functions for text manipulation and analysis
 * 
 * Provides efficient text processing utilities optimized for legal document editing,
 * including word boundary detection, legal text analysis, and performance-optimized operations.
 */

import { TextRange, LegalDocumentType } from '../types/DocumentTypes';

export class TextOperations {
  // ============================================================================
  // BASIC TEXT OPERATIONS
  // ============================================================================

  /**
   * Insert text at position with validation
   */
  static insertAt(text: string, position: number, insertion: string): string {
    const safePosition = Math.max(0, Math.min(position, text.length));
    return text.slice(0, safePosition) + insertion + text.slice(safePosition);
  }

  /**
   * Delete text range with validation
   */
  static deleteRange(text: string, start: number, end: number): { result: string; deleted: string } {
    const safeStart = Math.max(0, start);
    const safeEnd = Math.min(end, text.length);
    
    if (safeStart >= safeEnd) {
      return { result: text, deleted: '' };
    }
    
    const deleted = text.slice(safeStart, safeEnd);
    const result = text.slice(0, safeStart) + text.slice(safeEnd);
    
    return { result, deleted };
  }

  /**
   * Replace text range
   */
  static replaceRange(text: string, start: number, end: number, replacement: string): string {
    const { result } = this.deleteRange(text, start, end);
    return this.insertAt(result, start, replacement);
  }

  // ============================================================================
  // WORD BOUNDARY DETECTION
  // ============================================================================

  /**
   * Find word boundaries for smart navigation and selection
   */
  static findWordBoundaries(text: string, position: number): { start: number; end: number } {
    const wordRegex = /\b/g;
    const boundaries: number[] = [];
    let match;
    
    // Find all word boundaries
    while ((match = wordRegex.exec(text)) !== null) {
      boundaries.push(match.index);
    }
    
    // Add document start and end
    boundaries.unshift(0);
    boundaries.push(text.length);
    
    // Remove duplicates and sort
    const uniqueBoundaries = [...new Set(boundaries)].sort((a, b) => a - b);
    
    // Find boundaries around position
    let start = 0;
    let end = text.length;
    
    for (let i = 0; i < uniqueBoundaries.length - 1; i++) {
      if (position >= uniqueBoundaries[i] && position <= uniqueBoundaries[i + 1]) {
        start = uniqueBoundaries[i];
        end = uniqueBoundaries[i + 1];
        break;
      }
    }
    
    return { start, end };
  }

  /**
   * Get word at position
   */
  static getWordAt(text: string, position: number): { word: string; start: number; end: number } {
    const boundaries = this.findWordBoundaries(text, position);
    const word = text.slice(boundaries.start, boundaries.end).trim();
    
    return {
      word,
      start: boundaries.start,
      end: boundaries.end
    };
  }

  /**
   * Move to next word boundary
   */
  static findNextWordBoundary(text: string, position: number): number {
    const wordRegex = /\b\w/g;
    wordRegex.lastIndex = position + 1;
    
    const match = wordRegex.exec(text);
    return match ? match.index : text.length;
  }

  /**
   * Move to previous word boundary
   */
  static findPreviousWordBoundary(text: string, position: number): number {
    const beforeText = text.slice(0, position);
    const wordRegex = /\b\w/g;
    let lastMatch = 0;
    let match;
    
    while ((match = wordRegex.exec(beforeText)) !== null) {
      lastMatch = match.index;
    }
    
    return lastMatch;
  }

  // ============================================================================
  // LINE OPERATIONS
  // ============================================================================

  /**
   * Find line boundaries
   */
  static findLineBoundaries(text: string, position: number): { start: number; end: number } {
    const beforeText = text.slice(0, position);
    const afterText = text.slice(position);
    
    const lineStart = beforeText.lastIndexOf('\n') + 1;
    const lineEndIndex = afterText.indexOf('\n');
    const lineEnd = lineEndIndex === -1 ? text.length : position + lineEndIndex;
    
    return { start: lineStart, end: lineEnd };
  }

  /**
   * Get line at position
   */
  static getLineAt(text: string, position: number): { line: string; start: number; end: number } {
    const boundaries = this.findLineBoundaries(text, position);
    const line = text.slice(boundaries.start, boundaries.end);
    
    return {
      line,
      start: boundaries.start,
      end: boundaries.end
    };
  }

  /**
   * Count lines in text
   */
  static countLines(text: string): number {
    return text.split('\n').length;
  }

  /**
   * Get line number for position (1-based)
   */
  static getLineNumber(text: string, position: number): number {
    const beforeText = text.slice(0, position);
    return beforeText.split('\n').length;
  }

  // ============================================================================
  // LEGAL DOCUMENT ANALYSIS
  // ============================================================================

  /**
   * Detect legal citations in text
   */
  static findLegalCitations(text: string): TextRange[] {
    const citationPatterns = [
      /\d+\s+[A-Z][a-z]+\.?\s+\d+/g,           // "123 F.3d 456"
      /\d+\s+U\.S\.C\.?\s+§?\s*\d+/g,          // "42 U.S.C. § 1983"
      /\d+\s+[A-Z][a-z]+\.?\s+\d+d?\s+\d+/g,   // "123 Cal.App.4th 567"
    ];
    
    const citations: TextRange[] = [];
    
    for (const pattern of citationPatterns) {
      let match;
      while ((match = pattern.exec(text)) !== null) {
        citations.push({
          start: match.index,
          end: match.index + match[0].length
        });
      }
    }
    
    return citations;
  }

  /**
   * Detect contract clauses
   */
  static findContractClauses(text: string): TextRange[] {
    const clausePatterns = [
      /\b\d+\.\d+\b/g,                         // "1.1", "2.3"
      /\b[A-Z][A-Z\s]+\./g,                    // "WHEREAS.", "NOW THEREFORE."
      /\b(?:Section|Article|Clause)\s+\d+/gi,  // "Section 1", "Article II"
    ];
    
    const clauses: TextRange[] = [];
    
    for (const pattern of clausePatterns) {
      let match;
      while ((match = pattern.exec(text)) !== null) {
        clauses.push({
          start: match.index,
          end: match.index + match[0].length
        });
      }
    }
    
    return clauses;
  }

  /**
   * Detect signature blocks
   */
  static findSignatureBlocks(text: string): TextRange[] {
    const signaturePatterns = [
      /By:\s*_+/gi,                            // "By: ___________"
      /Signature:\s*_+/gi,                     // "Signature: _____"
      /\b[A-Z][a-z]+\s+[A-Z][a-z]+,\s*Esq\./g, // "John Smith, Esq."
    ];
    
    const signatures: TextRange[] = [];
    
    for (const pattern of signaturePatterns) {
      let match;
      while ((match = pattern.exec(text)) !== null) {
        signatures.push({
          start: match.index,
          end: match.index + match[0].length
        });
      }
    }
    
    return signatures;
  }

  // ============================================================================
  // DOCUMENT STRUCTURE ANALYSIS
  // ============================================================================

  /**
   * Analyze document structure for legal document type
   */
  static analyzeDocumentStructure(text: string): {
    detectedType: LegalDocumentType;
    confidence: number;
    sections: DocumentStructureSection[];
  } {
    const sections = this.detectDocumentSections(text);
    const detectedType = this.classifyDocumentType(text, sections);
    const confidence = this.calculateTypeConfidence(text, detectedType);
    
    return { detectedType, confidence, sections };
  }

  private static detectDocumentSections(text: string): DocumentStructureSection[] {
    const sections: DocumentStructureSection[] = [];
    
    // Detect headers (all caps lines)
    const headerRegex = /^[A-Z\s]{10,}$/gm;
    let match;
    while ((match = headerRegex.exec(text)) !== null) {
      sections.push({
        type: 'header',
        start: match.index,
        end: match.index + match[0].length,
        content: match[0].trim()
      });
    }
    
    // Detect numbered sections
    const sectionRegex = /^\d+\.\s+[A-Z]/gm;
    while ((match = sectionRegex.exec(text)) !== null) {
      sections.push({
        type: 'numbered_section',
        start: match.index,
        end: match.index + match[0].length,
        content: match[0]
      });
    }
    
    return sections;
  }

  private static classifyDocumentType(text: string, sections: DocumentStructureSection[]): LegalDocumentType {
    const lowerText = text.toLowerCase();
    
    // Contract indicators
    if (lowerText.includes('whereas') || lowerText.includes('now therefore') || 
        lowerText.includes('agreement') || lowerText.includes('contract')) {
      return 'contract';
    }
    
    // Brief indicators
    if (lowerText.includes('memorandum') || lowerText.includes('brief') ||
        lowerText.includes('argument') || lowerText.includes('court')) {
      return 'brief';
    }
    
    // Motion indicators
    if (lowerText.includes('motion') || lowerText.includes('moves') ||
        lowerText.includes('respectfully requests')) {
      return 'motion';
    }
    
    // Default to contract for unknown types
    return 'contract';
  }

  private static calculateTypeConfidence(text: string, type: LegalDocumentType): number {
    // Simple confidence calculation based on keyword density
    const keywords = this.getKeywordsForType(type);
    const matches = keywords.filter(keyword => text.toLowerCase().includes(keyword)).length;
    
    return Math.min(matches / keywords.length, 1.0);
  }

  private static getKeywordsForType(type: LegalDocumentType): string[] {
    switch (type) {
      case 'contract':
        return ['agreement', 'whereas', 'now therefore', 'party', 'consideration'];
      case 'brief':
        return ['memorandum', 'argument', 'court', 'plaintiff', 'defendant'];
      case 'motion':
        return ['motion', 'moves', 'respectfully requests', 'relief', 'court'];
      default:
        return [];
    }
  }

  // ============================================================================
  // PERFORMANCE UTILITIES
  // ============================================================================

  /**
   * Efficient text search with position tracking
   */
  static searchText(text: string, searchTerm: string, caseSensitive: boolean = false): TextRange[] {
    const results: TextRange[] = [];
    const searchText = caseSensitive ? text : text.toLowerCase();
    const searchFor = caseSensitive ? searchTerm : searchTerm.toLowerCase();
    
    let startIndex = 0;
    while (startIndex < searchText.length) {
      const foundIndex = searchText.indexOf(searchFor, startIndex);
      if (foundIndex === -1) break;
      
      results.push({
        start: foundIndex,
        end: foundIndex + searchTerm.length
      });
      
      startIndex = foundIndex + 1;
    }
    
    return results;
  }

  /**
   * Calculate text statistics for legal documents
   */
  static calculateTextStatistics(text: string): TextStatistics {
    return {
      characterCount: text.length,
      wordCount: this.countWords(text),
      paragraphCount: this.countParagraphs(text),
      pageCount: this.estimatePageCount(text),
      readingTime: this.estimateReadingTime(text),
      legalElements: {
        citations: this.findLegalCitations(text).length,
        clauses: this.findContractClauses(text).length,
        signatures: this.findSignatureBlocks(text).length
      }
    };
  }

  private static countWords(text: string): number {
    return text.trim().split(/\s+/).filter(word => word.length > 0).length;
  }

  private static countParagraphs(text: string): number {
    return text.split('\n').filter(para => para.trim().length > 0).length;
  }

  private static estimatePageCount(text: string): number {
    const wordsPerPage = 250; // Legal document standard
    const wordCount = this.countWords(text);
    return Math.ceil(wordCount / wordsPerPage);
  }

  private static estimateReadingTime(text: string): number {
    const wordsPerMinute = 200; // Legal document reading speed
    const wordCount = this.countWords(text);
    return Math.ceil(wordCount / wordsPerMinute);
  }
}

// ============================================================================
// SUPPORTING INTERFACES
// ============================================================================

interface DocumentStructureSection {
  type: 'header' | 'numbered_section' | 'clause' | 'signature';
  start: number;
  end: number;
  content: string;
}

interface TextStatistics {
  characterCount: number;
  wordCount: number;
  paragraphCount: number;
  pageCount: number;
  readingTime: number; // minutes
  legalElements: {
    citations: number;
    clauses: number;
    signatures: number;
  };
}
